import React, {Fragment, useState} from "react"

// MUI
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Fab from '@mui/material/Fab'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// MUI Icon
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'

// Custom Components
import Alert from "../Alert/Alert"
import ConfirmDialog from "../Dialog/ConfirmDialog"
import UserOption from "./UserOption"
import UserDataGrid from "./UserDataGrid"

// Custom Style
import shareMyListFormStyle from './styles/shareMyListFormStyle'

// Fetch Data
import {shareMyList} from "../../data_source/users"


const ShareMyListForm = props => {
	const {selectedListSeq, selectedMyListName, shareDialogClose} = props

	// Define State — search results
	const [userListResult, setUserListResult] = useState(null)
	const [userSeqSelected, setUserSeqSelected] = useState(null)
	const [userRecordSelected, setUserRecordSelected] = useState(null)

	// Define State — share target list
	const [shareUserSeq, setShareUserSeq] = useState([])
	const [shareUserRecord, setShareUserRecord] = useState([])
	const [shareTargetSeqSelected, setShareTargetSeqSelected] = useState([])
	const [shareTargetRecordSelected, setShareTargetRecordSelected] = useState([])

	// Define Alert
	const [alertOpen, setAlertOpen] = useState(false)
	const [alertData, setAlertData] = useState({
		type: "",
		title: "",
		content: ""
	})

	const buildAlert = (data) => {
		setAlertData({
			type: data.type,
			title: data.title,
			content: data.content
		})
		setAlertOpen(true)
	}

	// Define Confirm Dialog
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
	const [confirmDialogData, setConfirmDialogData] = useState({
		dialogTitle: "",
		dialogContent: "",
		confirmAction: "",
		cancelAction: ""
	})

	const confirmDialogHandleOpen = (data) => {
		setConfirmDialogData({
			dialogTitle: data.dialogTitle,
			dialogContent: data.dialogContent,
			confirmAction: data.confirmAction,
			cancelAction: data.cancelAction
		})
		setConfirmDialogOpen(true)
	}

	const confirmDialogHandleClose = () => {
		setConfirmDialogOpen(false)
	}


	// Add selected search-result users to the share target list
	const addShareUserAction = () => {
		if (userSeqSelected && userSeqSelected.length > 0) {
			const newSeq = userSeqSelected.filter(seq => !shareUserSeq.includes(seq))
			const newRecord = (userRecordSelected || []).filter(row => !shareUserSeq.includes(row.id))

			setShareUserSeq([...shareUserSeq, ...newSeq])
			setShareUserRecord([...shareUserRecord, ...newRecord])
		}

		// Force re-render of search results grid
		const res = userListResult
		setUserListResult([])
		setUserListResult(res)
	}


	// Remove selected share-target users from the share target list
	const removeShareUserAction = () => {
		const remainingSeq = shareUserSeq.filter(seq => !shareTargetSeqSelected.includes(seq))
		const remainingRecord = shareUserRecord.filter(row => !shareTargetRecordSelected.includes(row))

		setShareUserSeq(remainingSeq)
		setShareUserRecord(remainingRecord)
	}


	// Execute the share API call
	const shareAction = async () => {
		confirmDialogHandleClose()

		try {
			await Promise.all(
				shareUserSeq.map(targetUserSeq =>
					shareMyList(selectedListSeq, targetUserSeq)
				)
			)

			buildAlert({
				type: "success",
				title: "Success",
				content: "My List shared successfully."
			})

			setTimeout(() => shareDialogClose(), 1500)
		} catch {
			buildAlert({
				type: "error",
				title: "Error",
				content: "Failed to share My List. Please try again."
			})
		}
	}


	const shareConfirmHandleOpen = () => {
		confirmDialogHandleOpen({
			dialogTitle: "Share My List",
			dialogContent: `Are you sure to share "${selectedMyListName}" with the selected users?`,
			confirmAction: shareAction,
			cancelAction: confirmDialogHandleClose
		})
	}


	return (
		<Fragment>
			<Box>
				<Alert
					open={alertOpen}
					alertType={alertData.type}
					alertTitle={alertData.title}
					alertContent={alertData.content}
					handleClose={() => setAlertOpen(false)}
				/>

				<ConfirmDialog
					open={confirmDialogOpen}
					dialogTitle={confirmDialogData.dialogTitle}
					dialogContent={confirmDialogData.dialogContent}
					confirmAction={confirmDialogData.confirmAction}
					cancelAction={confirmDialogData.cancelAction}
				/>
			</Box>

			<Stack spacing={2}>
				<Box sx={shareMyListFormStyle.optionArea}>
					<UserOption
						setUserResult={setUserListResult}
					/>
				</Box>

				<Box sx={shareMyListFormStyle.tableArea}>
					<Stack spacing={2}>
						{
							userListResult ?
								<Box sx={shareMyListFormStyle.userListArea}>
									<Box sx={shareMyListFormStyle.userListTool}>
										<Tooltip title="Add User to Share List">
											<Fab color="primary" size="small" aria-label="add" onClick={addShareUserAction} sx={shareMyListFormStyle.normalBtnStyle}>
												<PersonAddAltOutlinedIcon />
											</Fab>
										</Tooltip>
									</Box>

									<Box>
										<UserDataGrid
											userList={userListResult}
											setUserSeq={setUserSeqSelected}
											setUserRecord={setUserRecordSelected}
										/>
									</Box>
								</Box>
							: ""
						}

						<Box sx={shareMyListFormStyle.resultArea}>
							<Box sx={shareMyListFormStyle.resultTitle}>
								Share "{selectedMyListName}" with
							</Box>

							{
								shareUserRecord.length > 0 ?
									<Box sx={shareMyListFormStyle.resultTool}>
										<Stack direction="row" spacing={2}>
											<Tooltip title="Remove User">
												<Fab color="primary" size="small" aria-label="remove" onClick={removeShareUserAction} sx={shareMyListFormStyle.normalBtnStyle}>
													<PersonRemoveOutlinedIcon />
												</Fab>
											</Tooltip>

											<Tooltip title="Share My List">
												<Fab color="primary" size="small" aria-label="share" onClick={shareConfirmHandleOpen} sx={shareMyListFormStyle.normalBtnStyle}>
													<IosShareOutlinedIcon />
												</Fab>
											</Tooltip>
										</Stack>
									</Box>
								: ""
							}

							<Box>
								<UserDataGrid
									userList={shareUserRecord}
									setUserSeq={setShareTargetSeqSelected}
									setUserRecord={setShareTargetRecordSelected}
								/>
							</Box>
						</Box>
					</Stack>
				</Box>
			</Stack>
		</Fragment>
	)
}

export default ShareMyListForm
